import React, { useEffect, useMemo, useState } from 'react';

import {
  Box,
  Button,
  HStack,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Stack,
  useBreakpointValue,
  VStack,
} from '@chakra-ui/react';
import { LoadingSpinner } from '@saas-ui/react';
import { RotateCwIcon, SlidersHorizontal, XIcon } from 'lucide-react';

import { SearchInput } from '@/components/common';
import { IconButton, WorkBeam } from '@/components/core';
import { libraryDocumentHref } from '@/components/features/ChatContainer/utils/libraryDocuments';
import { snackbar } from '@/components/features/Snackbar';
import { SnackbarType } from '@/components/features/Snackbar/Snackbar.contants';
import { HIGHEST_Z_INDEX, ROUTES } from '@/constant';
import { PARTS_CATALOG_AUTOCOMPLETE_MIN_CHARS } from '@/constant/quoteDocuments';
import { useStableNavigate } from '@/context/stableNavigate';
import { useAutocomplete } from '@/hooks/useAutocomplete';
import { useDocumentSearch } from '@/hooks/useDocumentSearch';
import { usePartsCatalogAutocomplete } from '@/hooks/usePartsCatalogAutocomplete';
import { useSearchOutcomes } from '@/hooks/useSearchOutcomes';
import { AutocompleteCandidate, SearchOutcomeHit } from '@/types/search';

import { DocumentsSection } from './DocumentsSection';
import {
  GLOBAL_SEARCH_FILTERS,
  GlobalSearchFilter,
  GlobalSearchFilterId,
} from './filters';
import { PartsPreviewSection } from './PartsPreviewSection';
import { SearchEmptyState } from './SearchEmptyState';
import { SearchFilterPills } from './SearchFilterPills';
import { SearchResultSections } from './SearchResultSections';
import { SimilarJobsSection } from './SimilarJobsSection';

/** One request per source after the user pauses. Parts uses the same pause. */
const DEBOUNCE_MS = 300;

/** Under "Search for all": 5 documents, 3 page rows each. */
const ALL_DOCUMENT_CAP = 5;
const ALL_DOCUMENT_PAGE_CAP = 3;
/** Under "Search for all": name matches leave room for semantic hits. */
const ALL_NAME_MATCH_CAP = 3;
/** Under "Knowledge base": documents only, up to 10. */
const KNOWLEDGE_DOCUMENT_CAP = 10;

export interface GlobalSearchOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  initialQuery?: string;
}

export const GlobalSearchOverlay: React.FC<GlobalSearchOverlayProps> = ({
  isOpen,
  onClose,
  initialQuery = '',
}) => {
  const navigate = useStableNavigate();
  const isMobile = useBreakpointValue({ base: true, xl: false });

  const [query, setQuery] = useState(initialQuery);
  const [debouncedQuery, setDebouncedQuery] = useState(initialQuery.trim());
  const [selectedFilterId, setSelectedFilterId] =
    useState<GlobalSearchFilterId>('all');

  const selectedFilter = useMemo(
    () =>
      GLOBAL_SEARCH_FILTERS.find(filter => filter.id === selectedFilterId) ??
      GLOBAL_SEARCH_FILTERS[0],
    [selectedFilterId]
  );

  const isPlaceholderFilter = Boolean(selectedFilter.isPlaceholder);
  const isPartsFilter = selectedFilterId === 'parts';
  const isKnowledgeFilter = selectedFilterId === 'knowledge';
  const isQueryReady =
    debouncedQuery.length >= (selectedFilter.minQueryLength ?? 2);
  const autocompleteEnabled =
    isOpen && !isPlaceholderFilter && !isPartsFilter && !isKnowledgeFilter;
  const outcomesEnabled =
    isOpen &&
    !isPlaceholderFilter &&
    (selectedFilterId === 'all' || selectedFilterId === 'jobs');
  const documentsEnabled =
    isOpen &&
    !isPlaceholderFilter &&
    (selectedFilterId === 'all' || isKnowledgeFilter);
  const partsEnabled =
    isOpen &&
    !isPlaceholderFilter &&
    (selectedFilterId === 'all' || isPartsFilter);

  const {
    groups,
    isLoading,
    isFetching,
    refetch: refetchAutocomplete,
  } = useAutocomplete({
    q: debouncedQuery,
    types: selectedFilter.types,
    enabled: autocompleteEnabled,
  });

  const {
    hits: outcomeHits,
    isLoading: isOutcomesLoading,
    isFetching: isOutcomesFetching,
    refetch: refetchOutcomes,
  } = useSearchOutcomes({
    queryText: debouncedQuery,
    enabled: outcomesEnabled,
  });

  const {
    hits: partsHits,
    matchesLiveQuery: partsMatchQuery,
    isLoading: isPartsLoading,
    isFetching: isPartsFetching,
    refetch: refetchParts,
  } = usePartsCatalogAutocomplete({
    q: query,
    enabled: partsEnabled,
  });

  const {
    results: documentResults,
    isLoading: isDocumentsLoading,
    isFetching: isDocumentsFetching,
    refetch: refetchDocuments,
  } = useDocumentSearch({
    queryText: debouncedQuery,
    enabled: documentsEnabled,
    documentCap: isKnowledgeFilter ? KNOWLEDGE_DOCUMENT_CAP : ALL_DOCUMENT_CAP,
    nameMatchCap: isKnowledgeFilter
      ? KNOWLEDGE_DOCUMENT_CAP
      : ALL_NAME_MATCH_CAP,
    pageCap: ALL_DOCUMENT_PAGE_CAP,
  });

  useEffect(() => {
    if (!isOpen) return;
    setQuery(initialQuery);
    setDebouncedQuery(initialQuery.trim());
    setSelectedFilterId('all');
  }, [isOpen, initialQuery]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedQuery(query.trim());
    }, DEBOUNCE_MS);

    return () => window.clearTimeout(timeoutId);
  }, [query]);

  const hasAutocompleteResults = useMemo(() => {
    if (!groups) return false;
    return (
      (groups.contacts?.length ?? 0) > 0 ||
      (groups.people?.length ?? 0) > 0 ||
      (groups.engineers?.length ?? 0) > 0 ||
      (groups.jobs?.length ?? 0) > 0
    );
  }, [groups]);

  const hasPartsResults = partsHits.length > 0;
  const showPartsSection =
    partsEnabled && query.trim().length >= PARTS_CATALOG_AUTOCOMPLETE_MIN_CHARS;
  const partsSectionLoading =
    showPartsSection &&
    !hasPartsResults &&
    (!partsMatchQuery || isPartsLoading || isPartsFetching);

  const hasOutcomeResults = outcomeHits.length > 0;
  const hasDocumentResults = documentResults.length > 0;
  const hasResults =
    hasAutocompleteResults ||
    hasOutcomeResults ||
    hasPartsResults ||
    hasDocumentResults;

  const autocompletePending =
    autocompleteEnabled &&
    (isLoading || (isFetching && !hasAutocompleteResults));
  const outcomesPending =
    outcomesEnabled &&
    (isOutcomesLoading || (isOutcomesFetching && !hasOutcomeResults));
  const documentsPending =
    documentsEnabled && isDocumentsLoading && !hasDocumentResults;
  const partsSettled =
    !showPartsSection ||
    (partsMatchQuery && !isPartsLoading && !isPartsFetching);

  const showEmpty =
    isQueryReady &&
    (isPlaceholderFilter ||
      (!autocompletePending &&
        !outcomesPending &&
        !documentsPending &&
        partsSettled &&
        !hasResults));

  const showFullSpinner =
    !isPartsFilter &&
    !isKnowledgeFilter &&
    !hasResults &&
    autocompletePending &&
    !outcomesPending;

  const isRefreshing =
    (autocompleteEnabled && isFetching) ||
    (outcomesEnabled && isOutcomesFetching) ||
    (documentsEnabled && isDocumentsFetching) ||
    (partsEnabled && isPartsFetching);
  const isSearchLive = isQueryReady && isRefreshing;

  const handleFilterSelect = (filter: GlobalSearchFilter) => {
    setSelectedFilterId(filter.id);
  };

  const handleRefresh = () => {
    if (autocompleteEnabled) {
      void refetchAutocomplete();
    }
    if (outcomesEnabled) {
      void refetchOutcomes();
    }
    if (documentsEnabled) {
      void refetchDocuments();
    }
    if (partsEnabled) {
      void refetchParts();
    }
  };

  const handleSelect = (candidate: AutocompleteCandidate) => {
    const destination = resolveCandidateDestination(candidate);

    if (!destination) {
      snackbar({
        type: SnackbarType.info,
        title: 'Coming soon',
        description:
          'This result type does not have a detail view yet. We will add it in a future update.',
      });
      return;
    }

    onClose();
    navigate(destination);
  };

  const handleOutcomeSelect = (hit: SearchOutcomeHit) => {
    onClose();
    navigate(ROUTES.JOB_DETAILS(String(hit.jobId)));
  };

  const handleDocumentSelect = (documentId: string, page?: number) => {
    onClose();
    navigate(libraryDocumentHref(documentId, page));
  };

  const handleOpenJobById = (jobId: number) => {
    onClose();
    navigate(ROUTES.JOB_DETAILS(String(jobId)));
  };

  const handleOpenAdvancedSearch = () => {
    const params = new URLSearchParams();
    const currentQuery = query.trim();
    if (currentQuery.length >= 2) {
      params.set('q', currentQuery);
    }
    const queryString = params.toString();
    onClose();
    navigate(queryString ? `${ROUTES.SEARCH}?${queryString}` : ROUTES.SEARCH, {
      state: { searchEntry: Date.now() },
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={isMobile ? 'full' : '6xl'}
      scrollBehavior='inside'
      motionPreset={isMobile ? 'slideInBottom' : 'scale'}
      blockScrollOnMount
      returnFocusOnClose={false}
    >
      <ModalOverlay bg='blackAlpha.700' backdropFilter='blur(2px)' />
      <ModalContent
        bg={{ base: 'app-background', xl: 'cards.bg' }}
        border={{ base: 'none', xl: '1px solid' }}
        borderColor='border.light'
        borderRadius={{ base: 0, xl: '2xl' }}
        my={{ base: 0, xl: 8 }}
        maxW={{ base: '100vw', xl: '1152px' }}
        w={{ base: '100%', xl: '1152px' }}
        minW={{ base: '100%', xl: '1152px' }}
        h={{ base: '100dvh', xl: 'auto' }}
        maxH={{ base: '100dvh', xl: 'calc(100dvh - 4rem)' }}
        overflow='hidden'
        zIndex={HIGHEST_Z_INDEX}
        data-cy='global-search-overlay'
      >
        <ModalBody
          p={{ base: 4, xl: 8 }}
          display='flex'
          flexDirection='column'
          gap={2}
          minW={0}
          overflowX='hidden'
        >
          <Stack gap={2} w='full' minW={0}>
            <HStack gap={2} w='full' align='flex-start' minW={0}>
              <Box flex={1} minW={0}>
                <WorkBeam preset='line' active={isSearchLive} borderRadius={8}>
                  <SearchInput
                    key={`global-search-${isOpen}-${initialQuery}`}
                    initialQuery={initialQuery}
                    enableAutoComplete={false}
                    debounceMs={0}
                    onQueryChange={setQuery}
                    onSearch={setQuery}
                    placeholder='Search for contacts, jobs, documents...'
                    maxWidth='full'
                    data-cy='global-search-input'
                  />
                </WorkBeam>
              </Box>
              <IconButton
                aria-label='Refresh search'
                variant='glass-item'
                size='sm'
                height={9}
                width={9}
                minWidth={9}
                icon={<RotateCwIcon size={16} />}
                isLoading={isRefreshing}
                isDisabled={!isQueryReady || isPlaceholderFilter}
                onClick={handleRefresh}
                data-cy='global-search-refresh'
              />
              <IconButton
                aria-label='Close search'
                variant='glass-item'
                size='sm'
                height={9}
                width={9}
                minWidth={9}
                icon={<XIcon size={16} />}
                onClick={onClose}
                data-cy='global-search-close'
              />
            </HStack>
            <HStack
              w='full'
              minW={0}
              align='center'
              justify='space-between'
              gap={2}
            >
              <Box flex={1} minW={0}>
                <SearchFilterPills
                  selectedId={selectedFilterId}
                  onSelect={handleFilterSelect}
                />
              </Box>
              <Button
                size='sm'
                variant='ghost'
                flexShrink={0}
                leftIcon={<SlidersHorizontal size={14} />}
                onClick={handleOpenAdvancedSearch}
                data-cy='global-search-advanced'
              >
                Advanced search
              </Button>
            </HStack>
          </Stack>

          <Box
            flex={1}
            minH={0}
            minW={0}
            w='full'
            overflowY='auto'
            overflowX='hidden'
          >
            {!isQueryReady ? null : isPlaceholderFilter || showEmpty ? (
              <SearchEmptyState />
            ) : showFullSpinner ? (
              <HStack justify='center' py={16}>
                <LoadingSpinner size='md' />
              </HStack>
            ) : (
              <VStack align='stretch' gap={4} w='full' minW={0}>
                {autocompleteEnabled ? (
                  <SearchResultSections
                    groups={groups}
                    onSelect={handleSelect}
                  />
                ) : null}
                {documentsEnabled ? (
                  <DocumentsSection
                    results={documentResults}
                    isLoading={documentsPending}
                    onSelect={handleDocumentSelect}
                  />
                ) : null}
                {showPartsSection ? (
                  <PartsPreviewSection
                    hits={partsHits}
                    isLoading={partsSectionLoading}
                  />
                ) : null}
                {outcomesEnabled ? (
                  <SimilarJobsSection
                    hits={outcomeHits}
                    queryText={debouncedQuery}
                    isLoading={outcomesPending}
                    onSelect={handleOutcomeSelect}
                    onOpenJob={handleOpenJobById}
                  />
                ) : null}
              </VStack>
            )}
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

const resolveCandidateDestination = (
  candidate: AutocompleteCandidate
): string | null => {
  const id = String(candidate.id);

  switch (candidate.type) {
    case 'job':
      return ROUTES.JOB_DETAILS(id);
    case 'contact':
      return `/contacts/${id}`;
    case 'person': {
      if (
        candidate.linkedContactId != null &&
        String(candidate.linkedContactId).length > 0
      ) {
        return `/contacts/${candidate.linkedContactId}`;
      }
      return null;
    }
    case 'engineer':
      // Engineers/resources detail UI is not shipped yet.
      return null;
    case 'job_type':
    case 'job_category':
    case 'contact_group':
    case 'resource_group':
      return null;
    default: {
      const _exhaustive: never = candidate.type;
      return _exhaustive;
    }
  }
};
